import { useState } from "react"

import parse from "html-react-parser"
import { useTranslation } from "react-i18next"

import { Orbit } from "@/components/icons"
import { buttonVariants } from "@/components/ui/button-variants"
import { Toggle } from "@/components/ui/toggle"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { CourseList } from "@/features/training/components/course-list"
import {
  DemoFooterToolbar,
  DemoMemoryPanel,
  DemoModelSelector,
} from "@/features/training/components/demo"
import { CourseLayout } from "@/features/training/components/layouts/course-layout"
import { CourseSection } from "@/features/training/components/layouts/course-section"
import { ensureArray } from "@/features/training/utils/ensure-array"
import { cn } from "@/lib/utils"

export function AIExplorerGettingStartedPage() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-explorer.getting-started" })
  const { t: tHist } = useTranslation("", { keyPrefix: "training.ai-explorer.history" })
  const { t: tBP } = useTranslation("", { keyPrefix: "training.ai-explorer.best-practices" })
  const [highReasoningEnabled, setHighReasoningEnabled] = useState(false)

  return (
    <CourseLayout>
      <CourseSection>
        <h1>{t("title")}</h1>
        <h2>{t("purpose.title")}</h2>
        <p>{parse(t("purpose.description"))}</p>
        <p>
          <strong>{t("purpose.what_you_gain")}</strong>
        </p>
        <CourseList data={ensureArray<string>(t("purpose.benefits", { returnObjects: true }))} />
        <p>
          <strong>{t("purpose.level")}</strong>
        </p>
      </CourseSection>

      <CourseSection>
        <h2>{t("learning_objectives.title")}</h2>
        <p>{t("learning_objectives.description")}</p>
        <CourseList
          data={ensureArray<string>(t("learning_objectives.objectives", { returnObjects: true }))}
        />
      </CourseSection>

      <CourseSection>
        <h2>{t("what_is.title")}</h2>
        <p>{parse(t("what_is.description"))}</p>
      </CourseSection>

      <CourseSection>
        <h2>{t("where_to_find.title")}</h2>
        <CourseList
          data={ensureArray<string>(t("where_to_find.locations", { returnObjects: true }))}
        />
        {/* AI Explorer interface is best shown via screenshot showing full layout */}
      </CourseSection>

      <CourseSection>
        <h2>{t("how_it_works.title")}</h2>
        <p>{parse(t("how_it_works.description"))}</p>
        <p>
          <strong>{t("how_it_works.use_when_label")}</strong>
        </p>
        <CourseList
          data={ensureArray<string>(t("how_it_works.use_when", { returnObjects: true }))}
        />
        <p>
          <strong>{t("how_it_works.how_to_label")}</strong>
        </p>
        <CourseList data={ensureArray<string>(t("how_it_works.steps", { returnObjects: true }))} />

        <h3>{t("how_it_works.switch_model.title")}</h3>
        <CourseList
          data={ensureArray<string>(
            t("how_it_works.switch_model.options", { returnObjects: true })
          )}
        />
        <div className="my-4">
          <DemoModelSelector />
        </div>

        <h3>{t("how_it_works.high_reasoning.title")}</h3>
        <p>{parse(t("how_it_works.high_reasoning.description"))}</p>
        <div className="my-4 flex items-center gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Toggle
                pressed={highReasoningEnabled}
                className={cn(
                  buttonVariants({ size: "icon", variant: "icon" }),
                  "size-7 min-w-7",
                  highReasoningEnabled &&
                    "border-foreground bg-secondary text-secondary-foreground border"
                )}
                onPressedChange={setHighReasoningEnabled}
              >
                <Orbit className="size-5" />
              </Toggle>
            </TooltipTrigger>
            <TooltipContent className="max-w-[250px]" side="bottom" sideOffset={5}>
              <p>{t("how_it_works.high_reasoning.tooltip")}</p>
            </TooltipContent>
          </Tooltip>
          <span className="text-muted-foreground text-sm">
            {t("how_it_works.high_reasoning.label")}
          </span>
        </div>
        <p>
          <strong>{t("how_it_works.high_reasoning.use_when_label")}</strong>
        </p>
        <CourseList
          data={ensureArray<string>(
            t("how_it_works.high_reasoning.use_when", { returnObjects: true })
          )}
        />
        <p>
          <strong>{t("how_it_works.high_reasoning.consider_label")}</strong>
        </p>
        <CourseList
          data={ensureArray<string>(
            t("how_it_works.high_reasoning.considerations", { returnObjects: true })
          )}
        />

        <h3>{t("how_it_works.tools_dropdown.title")}</h3>
        <p>{parse(t("how_it_works.tools_dropdown.description"))}</p>
        <div className="my-4">
          <DemoFooterToolbar />
        </div>
      </CourseSection>

      <CourseSection>
        <h2>{t("examples.title")}</h2>
        {ensureArray<{ title: string; points: string[]; effect_label: string; effect: string }>(
          t("examples.use_cases", { returnObjects: true })
        ).map((useCase, index) => (
          <div key={index}>
            <h3>{useCase.title}</h3>
            <CourseList data={ensureArray<string>(useCase.points)} />
            <p>
              <strong>{useCase.effect_label}</strong> {parse(useCase.effect)}
            </p>
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("exercises.title")}</h2>
        <p>{parse(t("exercises.intro"))}</p>
        {ensureArray<{ title: string; steps: string[] }>(
          t("exercises.items", { returnObjects: true })
        ).map((exercise, index) => (
          <div key={index}>
            <h3>{exercise.title}</h3>
            <CourseList data={ensureArray<string>(exercise.steps)} />
          </div>
        ))}
      </CourseSection>

      <CourseSection>
        <h2>{t("pitfalls.title")}</h2>
        <CourseList data={ensureArray<string>(t("pitfalls.tips", { returnObjects: true }))} />
      </CourseSection>

      {/* Memory — from History module */}
      <CourseSection>
        <h2>{tHist("memory.title")}</h2>
        <p>{parse(tHist("memory.description"))}</p>
        <CourseList data={ensureArray<string>(tHist("memory.features", { returnObjects: true }))} />
        <div className="my-4">
          <DemoMemoryPanel />
        </div>
      </CourseSection>

      {/* Best Practices — quick checklist */}
      <CourseSection>
        <h2>{tBP("quick_check.title")}</h2>
        <CourseList
          data={ensureArray<string>(tBP("quick_check.checklist", { returnObjects: true }))}
        />
      </CourseSection>

      <CourseSection>
        <h2>{t("summary.title")}</h2>
        <CourseList data={ensureArray<string>(t("summary.points", { returnObjects: true }))} />
        <p>{parse(t("summary.next_module"))}</p>
      </CourseSection>
    </CourseLayout>
  )
}
